<template>
  <v-div class="MatchResult" mb-4 border-softened-bottom>
    <article>
      <nuxt-link v-if="isOriginPartner" :to="to">
        <MatchOverview :article="article" />
      </nuxt-link>
      <a v-else :href="to" target="_self">
        <MatchOverview :article="article" />
      </a>
      <v-div mt-3>
        <HighlightedText v-if="highlight" :highlight="highlight" />
      </v-div>
      <v-div v-if="displayKeywords" mt-3>
        <KeywordTags
          v-if="article.keywords && article.keywords.length > 0"
          :article="article"
          :show-label="false"
        />
      </v-div>
      <v-div clear-fix mt-3 mb-5 font-smaller overflow-hidden relative>
        <ToggleSection
          section-name="abstract"
          :bold-button-text="boldBottomSection"
        >
          <div class="pb-2 font-small">
            <Abstract :abstract="article.abstract" />
          </div>
        </ToggleSection>

        <v-div
          :font-weight-bold="boldBottomSection"
          float-left
          :has-action="fileSource !== undefined"
        >
          <button
            :aria-label="downloadAriaLabel"
            @click="onDownload"
            @keyup.enter="onDownload"
          >
            {{ isVideo ? "Play Video" : "Download" }}
          </button>
        </v-div>
        <v-div float-left border-softened-left ml-2 pl-2>
          <v-div d-inline :font-weight-bold="boldBottomSection">{{
            article.metrics
              | extractMetricsValue(showMetric)
              | localNumberWithZero
          }}</v-div>
          <v-div
            d-inline
            :muted--text="!boldBottomSection"
            :partially-transparent="!boldBottomSection"
            >{{ showMetric | extractMetricsName }}</v-div
          >
        </v-div>
        <v-div
          v-if="displayCitationsCount"
          float-left
          border-softened-left
          ml-2
          pl-2
        >
          <v-div d-inline :font-weight-bold="boldBottomSection">{{
            article.metrics
              | extractMetricsValue("CITATION_COUNT")
              | localNumberWithZero
          }}</v-div>
          <v-div
            d-inline
            :muted--text="!boldBottomSection"
            :partially-transparent="!boldBottomSection"
            >Citations</v-div
          >
        </v-div>
        <v-div float-right bottom="0">
          <SmallPartnerLogo :origin="article.origin" />
        </v-div>
      </v-div>
    </article>
  </v-div>
</template>

<script>
import { mapState } from "vuex";

import SmallPartnerLogo from "../banner/SmallPartnerLogo.vue";

import {
  displayIsMobile,
  displayIsTablet,
  displayIsDesktop,
} from "~/components/utilities/displayBreakpoints";

import Abstract from "~/components/articles/components/Abstract";
import MatchOverview from "~/components/matches/MatchOverview.vue";
import HighlightedText from "~/components/matches/components/HighlightedText.vue";
import KeywordTags from "~/components/articles/components/KeywordTags.vue";
import ToggleSection from "~/components/page/components/buttons/ToggleSection.vue";

export default {
  name: "MatchResult",
  components: {
    Abstract,
    HighlightedText,
    KeywordTags,
    MatchOverview,
    SmallPartnerLogo,
    ToggleSection,
  },
  props: {
    highlight: { type: Object, default: undefined },
    article: { type: Object, required: true },
    showMetric: { type: String, default: "MAIN_ITEM_DOWNLOAD_COUNT" },
    displayKeywords: { type: Boolean, default: false },
  },
  computed: {
    ...mapState({
      partners: (state) => state.theme.partners,
      layout: (state) => state.theme.layout,
    }),
    boldBottomSection() {
      return this.layout.matchResultBoldBottomSection;
    },
    fileSource() {
      if (this.article && this.article.asset && this.article.asset.original) {
        return this.article.asset.original.url;
      }
      return undefined;
    },
    displayIsMobile,
    displayIsTablet,
    displayIsDesktop,
    partner() {
      return this.$config.partner;
    },
    smallLogo() {
      const origin = this.article?.origin || "";
      const source = this.partners
        ? this.partners[origin.toLowerCase()]
        : undefined;

      return source ? source.logo : undefined;
    },
    isOriginPartner() {
      return this.article?.origin?.toLowerCase() === this.partner.toLowerCase();
    },
    to() {
      return this.isOriginPartner
        ? `/engage/${this.partner}/article-details/${this.article.id}`
        : `/engage/${this.article.origin.toLowerCase()}/article-details/${
            this.article.id
          }`;
    },
    isVideo() {
      return this.fileSource ? this.fileSource.includes(".mp4") : false;
    },
    downloadAriaLabel() {
      return `${this.isVideo ? "Play Video" : "Download"} ${
        this.article.title
      } ${!this.isVideo && "[opens in a new tab]"}`;
    },
    displayCitationsCount() {
      return this.article.citationsCount;
    },
  },
  methods: {
    onDownload() {
      const url = this.isVideo ? this.to + "?playVideo=true" : this.fileSource;

      if (url) {
        window.open(url, "_blank");
      }
    },
  },
};
</script>

<style>
.MatchResult a {
  color: inherit;
  text-decoration: none;
}

.MatchResult img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.MatchResult .Abstract {
  border: 1px solid #ececec;
}
</style>
